import { ComponentRef, FormEvent, ReactElement, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Checkbox,
  ErrorSummary,
  Field,
  Fieldset,
  Input,
  Label,
  Paragraph,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import { AuthLayout } from '../../components/AuthLayout';
import {
  getLegalDocuments,
  resendSignupCode,
  startSignup,
  verifySignup,
  type LegalDocumentKind,
} from '../../api/signup';
import { Routes } from '../../enums/Routes.ts';
import { CompanyPicker } from './CompanyPicker';
import { Perks } from './Perks';
import {
  codeError,
  CodeError,
  EMPTY_VALUES,
  FIELD_IDS,
  forgetPending,
  pendingEmail,
  rememberPending,
  serverError,
  SignUpError,
  SignUpField,
  SignUpValues,
  validateSignUp,
} from './signUpView';
import classes from './SignUp.module.css';

type ErrorData = { errorCode?: string; code?: string; attemptsLeft?: number; fields?: string[] };
const dataOf = (error: unknown) => (error as { response?: { data?: ErrorData } }).response?.data;

/**
 * «Prøv gratis». The form first, then the code sent by email. Nothing is made until the code is
 * confirmed, and the password stays in the browser until then.
 */
export const SignUpPage = (): ReactElement => {
  const { t } = useTranslation();
  const [values, setValues] = useState<SignUpValues>(EMPTY_VALUES);
  const [codeFor, setCodeFor] = useState<string | null>(pendingEmail);
  // What the code step found taken, shown at its field in the form
  const [taken, setTaken] = useState<SignUpError | null>(null);

  return codeFor ? (
    <CodeStep
      email={codeFor}
      password={values.password}
      onBack={() => {
        forgetPending();
        setCodeFor(null);
      }}
      onTaken={(error) => {
        forgetPending();
        setTaken(error);
        setCodeFor(null);
      }}
      onNewVersion={() => {
        forgetPending();
        setValues((current) => ({ ...current, terms: false }));
        setCodeFor(null);
      }}
    />
  ) : (
    <AccountStep
      values={values}
      setValues={setValues}
      initialErrors={taken ? [taken] : []}
      onSent={(email) => {
        setTaken(null);
        rememberPending(email);
        setCodeFor(email);
      }}
      title={t('auth.signUp.title')}
    />
  );
};

const AccountStep = ({
  values,
  setValues,
  initialErrors,
  onSent,
  title,
}: {
  values: SignUpValues;
  setValues: (update: (current: SignUpValues) => SignUpValues) => void;
  /** An error from the code step, such as the email being taken in the meantime */
  initialErrors: SignUpError[];
  onSent: (email: string) => void;
  title: string;
}): ReactElement => {
  const { t, i18n } = useTranslation();
  const language = i18n.language === 'en' ? 'en' : 'nb';
  // In the language of the page, so the user accepts the documents in the language they read them in
  const documents = useQuery({
    queryKey: ['legal-documents', 'current', language],
    queryFn: () => getLegalDocuments(language),
    staleTime: 5 * 60_000,
  });
  const start = useMutation({ mutationFn: (body: Parameters<typeof startSignup>[0]) => startSignup(body) });
  const [errors, setErrors] = useState<SignUpError[]>(initialErrors);
  const [summaryTick, setSummaryTick] = useState(initialErrors.length > 0 ? 1 : 0);
  const summaryRef = useRef<ComponentRef<typeof ErrorSummary>>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (summaryTick > 0) summaryRef.current?.focus();
  }, [summaryTick]);

  const errorFor = (field: SignUpField) => errors.find((error) => error.field === field);
  const general = errors.find((error) => error.field === 'general');
  const fieldErrors = errors.filter((error) => error.field !== 'general');
  // A field the user changes loses its error, so the list shows only what is still wrong
  const set = <K extends keyof SignUpValues>(key: K, value: SignUpValues[K]) => {
    setValues((current) => ({ ...current, [key]: value }));
    setErrors((current) => current.filter((error) => error.field !== key && error.field !== 'general'));
  };
  const versionOf = (kind: LegalDocumentKind) => documents.data?.find((document) => document.kind === kind)?.version;
  const terms = documents.data?.find((document) => document.kind === 'TERMS');
  const dpa = documents.data?.find((document) => document.kind === 'DPA');
  const unavailable = documents.isSuccess && (!versionOf('TERMS') || !versionOf('DPA'));

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const found = validateSignUp(values);
    setErrors(found);
    if (found.length > 0) {
      setSummaryTick((tick) => tick + 1);
      return;
    }
    const company = values.company!;
    start.mutate(
      {
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        email: values.email.trim(),
        password: values.password,
        company:
          company.source === 'brreg'
            ? { source: 'brreg', orgNumber: company.orgNumber, name: company.name, employees: company.employees }
            : { source: 'manual', name: company.name },
        acceptedTerms: values.terms,
        acceptedVersions: { TERMS: versionOf('TERMS'), DPA: versionOf('DPA') },
        language,
      },
      {
        onSuccess: ({ email }) => onSent(email),
        onError: (error) => {
          const found = serverError(dataOf(error));
          if (found.field === 'terms') {
            // A new version was published: the user sees it and ticks again
            set('terms', false);
            void queryClient.invalidateQueries({ queryKey: ['legal-documents'] });
          }
          setErrors([found]);
          if (found.field !== 'general') setSummaryTick((tick) => tick + 1);
        },
      },
    );
  };

  const field = (
    name: 'firstName' | 'lastName' | 'email' | 'password' | 'repeatPassword',
    extra?: { hint?: string; type?: 'email' | 'password'; autoComplete?: string },
  ) => {
    const error = errorFor(name);
    const id = FIELD_IDS[name];
    const describedBy = [extra?.hint ? `${id}-hint` : '', error ? `${id}-error` : ''].filter(Boolean).join(' ');
    return (
      <Field>
        <Label htmlFor={id}>{t(`auth.signUp.fields.${name}`)}</Label>
        {extra?.hint && (
          <Paragraph data-size="sm" id={`${id}-hint`} className={classes.hint}>
            {extra.hint}
          </Paragraph>
        )}
        <Input
          id={id}
          name={name}
          type={extra?.type ?? 'text'}
          autoComplete={extra?.autoComplete}
          value={values[name]}
          onChange={(event) => set(name, event.target.value)}
          aria-invalid={!!error}
          aria-describedby={describedBy || undefined}
        />
        {error && <ValidationMessage id={`${id}-error`}>{t(error.message)}</ValidationMessage>}
      </Field>
    );
  };

  return (
    <AuthLayout
      wide
      title={title}
      lead={t('auth.signUp.lead')}
      aside={<Perks />}
      below={
        <Paragraph>
          {t('auth.signUp.haveAccount')}{' '}
          <Link to={Routes.Root} className="ds-link">
            {t('auth.signUp.login')}
          </Link>
        </Paragraph>
      }
    >
      {unavailable && (
        <Alert data-color="warning" role="alert">
          <Paragraph>{t('auth.signUp.errors.unavailable')}</Paragraph>
        </Alert>
      )}
      {general && (
        <Alert data-color="danger" role="alert">
          <Paragraph>{t(general.message)}</Paragraph>
        </Alert>
      )}
      {fieldErrors.length > 0 && (
        <ErrorSummary ref={summaryRef}>
          <ErrorSummary.Heading>{t('auth.errorSummary')}</ErrorSummary.Heading>
          <ErrorSummary.List>
            {fieldErrors.map((error) => (
              <ErrorSummary.Item key={error.field}>
                <ErrorSummary.Link
                  href={`#${FIELD_IDS[error.field as SignUpField]}`}
                  onClick={(event) => {
                    event.preventDefault();
                    document.getElementById(FIELD_IDS[error.field as SignUpField])?.focus();
                  }}
                >
                  {t(error.message)}
                </ErrorSummary.Link>
              </ErrorSummary.Item>
            ))}
          </ErrorSummary.List>
        </ErrorSummary>
      )}

      <form className={classes.form} onSubmit={submit} noValidate>
        <Fieldset className={classes.group}>
          <Fieldset.Legend>{t('auth.signUp.you')}</Fieldset.Legend>
          <div className={classes.row}>
            {field('firstName', { autoComplete: 'given-name' })}
            {field('lastName', { autoComplete: 'family-name' })}
          </div>
          {field('email', { type: 'email', autoComplete: 'email', hint: t('auth.signUp.emailHint') })}
          {field('password', { type: 'password', autoComplete: 'new-password', hint: t('auth.signUp.passwordHint') })}
          {field('repeatPassword', { type: 'password', autoComplete: 'new-password' })}
        </Fieldset>

        <Fieldset className={classes.group}>
          <Fieldset.Legend>{t('auth.signUp.company')}</Fieldset.Legend>
          <Fieldset.Description>{t('auth.signUp.companyHint')}</Fieldset.Description>
          <CompanyPicker
            id={FIELD_IDS.company}
            company={values.company}
            onChange={(company) => set('company', company)}
            error={errorFor('company') ? t(errorFor('company')!.message) : undefined}
          />
        </Fieldset>

        <Field>
          <Checkbox
            id={FIELD_IDS.terms}
            checked={values.terms}
            onChange={(event) => set('terms', event.target.checked)}
            aria-invalid={!!errorFor('terms')}
            aria-describedby={errorFor('terms') ? `${FIELD_IDS.terms}-error` : undefined}
            label={
              <>
                {t('auth.signUp.terms.accept')}{' '}
                <a href={terms?.url ?? Routes.Terms} target="_blank" rel="noreferrer" className="ds-link">
                  {t('auth.signUp.terms.terms')}
                  {terms?.url && (
                    <>
                      {' '}
                      {t('auth.signUp.terms.pdf', {
                        format: t(`auth.legal.pdfFormat.${terms.language}`),
                        version: terms.version,
                      })}
                    </>
                  )}
                </a>{' '}
                {t('auth.signUp.terms.and')}{' '}
                <a href={dpa?.url ?? Routes.DataProcessingAgreement} target="_blank" rel="noreferrer" className="ds-link">
                  {t('auth.signUp.terms.dpa')}
                  {dpa?.url && (
                    <>
                      {' '}
                      {t('auth.signUp.terms.pdf', {
                        format: t(`auth.legal.pdfFormat.${dpa.language}`),
                        version: dpa.version,
                      })}
                    </>
                  )}
                </a>{' '}
                {t('auth.signUp.terms.onBehalf')}
              </>
            }
          />
          {errorFor('terms') && (
            <ValidationMessage id={`${FIELD_IDS.terms}-error`}>{t(errorFor('terms')!.message)}</ValidationMessage>
          )}
        </Field>

        <Button type="submit" className={classes.fullWidth} loading={start.isPending} disabled={unavailable}>
          {t('auth.signUp.submit')}
        </Button>
      </form>
    </AuthLayout>
  );
};

const CodeStep = ({
  email,
  password,
  onBack,
  onTaken,
  onNewVersion,
}: {
  email: string;
  /** Empty after a reload: the user writes it again, as it was never stored */
  password: string;
  onBack: () => void;
  /** The email or the org number got an account after the code was sent */
  onTaken: (error: SignUpError) => void;
  onNewVersion: () => void;
}): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');
  const [typedPassword, setTypedPassword] = useState('');
  const [problem, setProblem] = useState<{ error: CodeError; attemptsLeft?: number } | null>(null);
  const verify = useMutation({ mutationFn: (body: Parameters<typeof verifySignup>[0]) => verifySignup(body) });
  const resend = useMutation({ mutationFn: () => resendSignupCode(email) });
  const K = 'auth.signUp.code';
  const needsPassword = !password;

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!/^\d{6}$/.test(code.replace(/\s/g, ''))) return setProblem({ error: 'format' });
    if (needsPassword && typedPassword.length < 8) return setProblem({ error: 'password' });
    setProblem(null);
    verify.mutate(
      { email, code: code.replace(/\s/g, ''), password: password || typedPassword },
      {
        onSuccess: () => {
          forgetPending();
          // The code logged the user in. The app asked before, while no one was, and must know it
          // now, or «Gå til Trustpulse» sends the new owner back to the login page.
          queryClient.setQueryData(['IsLoggedIn'], { isLoggedIn: true });
          // The new account goes on to the role and the first goal
          navigate(Routes.OnboardingRole);
        },
        onError: (error) => {
          const data = dataOf(error);
          if (data?.code === 'newVersion') return onNewVersion();
          const found = serverError(data);
          if (found.field === 'email' || found.field === 'company') return onTaken(found);
          setProblem(codeError(data));
        },
      },
    );
  };

  const message = problem
    ? problem.error === 'wrong'
      ? t(`${K}.errors.wrong`, { count: problem.attemptsLeft ?? 0 })
      : t(`${K}.errors.${problem.error}`)
    : null;
  const codeInvalid = problem && problem.error !== 'password' && problem.error !== 'general';

  return (
    <AuthLayout title={t(`${K}.title`)} lead={t(`${K}.lead`, { email })} aside={<Perks />}>
      <form className={classes.form} onSubmit={submit} noValidate>
        <Field>
          <Label htmlFor="signup-code">{t(`${K}.label`)}</Label>
          <Input
            id="signup-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={7}
            value={code}
            className={classes.code}
            onChange={(event) => setCode(event.target.value)}
            aria-invalid={codeInvalid || undefined}
            aria-describedby={codeInvalid ? 'signup-code-error' : undefined}
          />
          {codeInvalid && <ValidationMessage id="signup-code-error">{message}</ValidationMessage>}
        </Field>
        {problem?.error === 'expired' && (
          // The sign-up may be gone, and then no new code comes: the user can always start again
          <Button type="button" variant="secondary" className={classes.fullWidth} onClick={onBack}>
            {t(`${K}.startOver`)}
          </Button>
        )}
        {needsPassword && (
          <Field>
            <Label htmlFor="signup-code-password">{t('auth.signUp.fields.password')}</Label>
            <Paragraph data-size="sm" id="signup-code-password-hint" className={classes.hint}>
              {t(`${K}.passwordAgain`)}
            </Paragraph>
            <Input
              id="signup-code-password"
              type="password"
              autoComplete="new-password"
              value={typedPassword}
              onChange={(event) => setTypedPassword(event.target.value)}
              aria-invalid={problem?.error === 'password' || undefined}
              aria-describedby="signup-code-password-hint"
            />
            {problem?.error === 'password' && <ValidationMessage>{t('auth.signUp.errors.passwordLength')}</ValidationMessage>}
          </Field>
        )}
        {problem?.error === 'general' && (
          <Alert data-color="danger" role="alert">
            <Paragraph>{t('auth.signUp.errors.general')}</Paragraph>
          </Alert>
        )}
        <Button type="submit" className={classes.fullWidth} loading={verify.isPending}>
          {t(`${K}.confirm`)}
        </Button>
        <div className={classes.codeActions}>
          <Button
            type="button"
            variant="tertiary"
            data-size="sm"
            disabled={resend.isSuccess || resend.isPending}
            onClick={() =>
              resend.mutate(undefined, {
                onSuccess: () => setProblem(null),
                onError: (error) => setProblem(codeError(dataOf(error))),
              })
            }
          >
            {resend.isSuccess ? t(`${K}.resent`) : t(`${K}.resend`)}
          </Button>
          <Button type="button" variant="tertiary" data-size="sm" onClick={onBack}>
            {t(`${K}.back`)}
          </Button>
        </div>
        <div aria-live="polite" className="ds-sr-only">
          {resend.isSuccess ? t(`${K}.resent`) : ''}
        </div>
      </form>
    </AuthLayout>
  );
};
